import { useEffect, useState } from "react";
import { AppLayout } from "@/components/AppLayout";
import { useI18n } from "@/i18n/I18nContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Flame, Trophy, History, Medal, Target } from "lucide-react";
import { Avatar } from "@/components/Avatar";

const TYPE_LABEL = {
  load: { es: "Carga (lbs)", en: "Load (lbs)", fr: "Charge (lbs)", ph: "Ej: 185 lbs" },
  time: { es: "Tiempo", en: "Time", fr: "Temps", ph: "Ej: 12:45" },
  reps: { es: "Repeticiones", en: "Reps", fr: "Répétitions", ph: "Ej: 120" },
  free: { es: "Resultado", en: "Result", fr: "Résultat", ph: "..." },
};

const parseScore = (type, raw) => {
  if (raw === undefined || raw === null || String(raw).trim() === "") return null;
  const s = String(raw).trim();
  if (type === "time") {
    if (s.includes(":")) {
      const parts = s.split(":").map((x) => Number(x));
      if (parts.some((n) => Number.isNaN(n))) return null;
      return parts.reduce((a, b) => a * 60 + b, 0);
    }
  }
  const m = s.match(/-?\d+(\.\d+)?/);
  return m ? parseFloat(m[0]) : null;
};

// Rank participants for a block. time = lower is better; load/reps = higher; free = no ranking.
const rankBlock = (results, block) => {
  const rows = results
    .map((r) => ({ name: r.user_name, user_id: r.user_id, raw: r.values?.[block.block_id], level: r.level }))
    .filter((x) => x.raw !== undefined && x.raw !== null && String(x.raw).trim() !== "");
  if (block.result_type === "free") return rows.map((x) => ({ ...x, rank: null }));
  const scored = rows.map((x) => ({ ...x, score: parseScore(block.result_type, x.raw) }));
  const unranked = scored.filter((x) => x.score === null);
  const ranked = scored.filter((x) => x.score !== null)
    .sort((a, b) => (block.result_type === "time" ? a.score - b.score : b.score - a.score));
  return [...ranked.map((x, i) => ({ ...x, rank: i + 1 })), ...unranked.map((x) => ({ ...x, rank: null }))];
};

const RANK_STYLE = {
  1: "bg-gold text-gold-foreground",
  2: "bg-muted-foreground/30 text-foreground",
  3: "bg-[#cd7f32] text-white",
};

const LEVELS = [
  { key: "legend", label: "Legend", cls: "bg-gold text-gold-foreground border-gold" },
  { key: "fitness", label: "Fitness", cls: "bg-primary text-primary-foreground border-primary" },
  { key: "rookie", label: "Rookie", cls: "bg-secondary text-foreground border-border" },
];
const LEVEL_MAP = Object.fromEntries(LEVELS.map((l) => [l.key, l]));
const normLevel = (r) => (LEVEL_MAP[r.level] ? r.level : "fitness");


export function Wod() {
  const { t, tl, lang } = useI18n();
  const [wod, setWod] = useState(undefined);
  const [results, setResults] = useState([]);
  const [history, setHistory] = useState([]);
  const [values, setValues] = useState({});
  const [level, setLevel] = useState("fitness");
  const [note, setNote] = useState("");

  const loadResults = (id) => api.get(`/workouts/${id}/results`).then(({ data }) => setResults(data));
  const loadHistory = () => api.get("/workouts/history/me").then(({ data }) => setHistory(data));

  const load = async () => {
    const { data } = await api.get("/workouts/today");
    setWod(data);
    if (data) {
      if (data.my_result) { setValues(data.my_result.values || {}); setNote(data.my_result.note || ""); setLevel(data.my_result.level || "fitness"); }
      loadResults(data.workout_id);
    }
    loadHistory();
  };
  useEffect(() => { load(); }, []);

  const submit = async () => {
    const filled = Object.values(values).some((v) => v && v.trim());
    if (!filled) return;
    try {
      await api.post(`/workouts/${wod.workout_id}/result`, { values, level, note });
      toast.success(t("common.success"));
      loadResults(wod.workout_id);
      loadHistory();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const blocks = (wod && wod.blocks) || [];
  const blockTitle = (id) => blocks.find((b) => b.block_id === id)?.title || "";

  return (
    <AppLayout>
      <h1 className="mb-1 flex items-center gap-2 font-display text-3xl font-bold tracking-tight">
        <Flame className="h-7 w-7 text-primary" /> {tl({ es: "Rutina del día", en: "Workout of the day", fr: "Séance du jour" })}
      </h1>
      <p className="mb-6 text-sm text-muted-foreground">{new Date().toLocaleDateString(undefined, { weekday: "long", day: "numeric", month: "long", timeZone: "America/Panama" })}</p>

      {wod === undefined ? (
        <div className="py-16 text-center text-muted-foreground">{t("common.loading")}</div>
      ) : !wod ? (
        <div className="rounded-2xl border border-dashed border-border p-10 text-center text-muted-foreground">
          {tl({ es: "No hay rutina publicada hoy.", en: "No workout published today.", fr: "Aucune séance publiée aujourd'hui." })}
        </div>
      ) : (
        <div className="space-y-6">
          <div className="rounded-2xl border border-border bg-card p-6">
            <h2 className="font-display text-2xl font-bold">{wod.title}</h2>
            {wod.description && <p className="mt-2 whitespace-pre-wrap text-sm text-muted-foreground">{wod.description}</p>}
          </div>

          {(wod.focus_tip || wod.focus_video) && (
            <div className="rounded-2xl border border-gold/40 bg-gold/5 p-6" data-testid="wod-focus">
              <h3 className="flex items-center gap-2 font-display text-lg font-semibold text-gold">
                <Target className="h-5 w-5" /> {tl({ es: "Focus del día", en: "Daily Focus", fr: "Focus du jour" })}
              </h3>
              {wod.focus_tip && <p className="mt-2 whitespace-pre-wrap text-sm">{wod.focus_tip}</p>}
              {wod.focus_video && (wod.focus_video.provider === "youtube" || wod.focus_video.provider === "vimeo") && (
                <div className="mt-3 aspect-video w-full overflow-hidden rounded-xl border border-border sm:max-w-md">
                  <iframe src={wod.focus_video.embed_url} title={wod.focus_video.title} className="h-full w-full" allowFullScreen />
                </div>
              )}
            </div>
          )}

          {blocks.map((b, i) => {
            const rt = TYPE_LABEL[b.result_type] || TYPE_LABEL.free;
            return (
              <div key={b.block_id} data-testid={`wod-block-${b.block_id}`} className="rounded-2xl border border-border bg-card p-6">
                <div className="flex items-center gap-2">
                  <span className="flex h-6 w-6 items-center justify-center rounded-full bg-primary text-xs font-bold text-primary-foreground">{i + 1}</span>
                  <h3 className="font-display text-lg font-semibold">{b.title}</h3>
                </div>
                {b.description && <p className="mt-2 whitespace-pre-wrap text-sm text-muted-foreground">{b.description}</p>}
                <div className="mt-4">
                  <label className="text-sm font-medium">{rt[lang]}</label>
                  <Input
                    data-testid={`wod-value-${b.block_id}`}
                    value={values[b.block_id] || ""}
                    onChange={(e) => setValues((prev) => ({ ...prev, [b.block_id]: e.target.value }))}
                    placeholder={rt.ph} className="mt-1.5 h-12"
                  />
                </div>
              </div>
            );
          })}

          <div className="rounded-2xl border border-border bg-card p-6">
            <label className="text-sm font-medium">{tl({ es: "Nivel", en: "Level", fr: "Niveau" })}</label>
            <div className="mt-2 grid grid-cols-3 gap-2" data-testid="wod-level-selector">
              {LEVELS.map((l) => (
                <button key={l.key} data-testid={`wod-level-${l.key}`} onClick={() => setLevel(l.key)}
                  className={`rounded-full border px-3 py-2 text-sm font-semibold transition-all ${level === l.key ? l.cls + " scale-[1.03] shadow-sm" : "border-border bg-secondary/40 text-muted-foreground hover:bg-secondary"}`}>
                  {l.label}
                </button>
              ))}
            </div>
            <Textarea data-testid="wod-note" value={note} onChange={(e) => setNote(e.target.value)} placeholder={tl({ es: "Nota general (opcional)", en: "General note (optional)", fr: "Note générale (optionnel)" })} className="mt-4" />
            <Button data-testid="wod-submit" onClick={submit} className="mt-3 rounded-full">{t("common.save")}</Button>
          </div>

          <div className="rounded-2xl border border-border bg-card p-6" data-testid="wod-leaderboard">
            <h3 className="mb-4 flex items-center gap-2 font-display text-lg font-semibold"><Trophy className="h-5 w-5 text-gold" /> {tl({ es: "Clasificación del día", en: "Daily leaderboard", fr: "Classement du jour" })} · {results.length} {tl({ es: "participantes", en: "athletes", fr: "participants" })}</h3>
            {results.length === 0 ? (
              <p className="text-sm text-muted-foreground">{tl({ es: "Sé el primero en registrar tu resultado.", en: "Be the first to log your result.", fr: "Sois le premier à enregistrer ton résultat." })}</p>
            ) : (
              <div className="space-y-6">
                {blocks.map((b) => {
                  const anyRows = results.some((r) => r.values?.[b.block_id] && String(r.values[b.block_id]).trim() !== "");
                  return (
                    <div key={b.block_id} data-testid={`leaderboard-block-${b.block_id}`}>
                      <div className="mb-2 flex items-center justify-between">
                        <span className="font-display text-sm font-semibold">{b.title}</span>
                        <span className="text-xs text-muted-foreground">{(TYPE_LABEL[b.result_type] || TYPE_LABEL.free)[lang]}</span>
                      </div>
                      {!anyRows ? (
                        <p className="text-xs text-muted-foreground">{tl({ es: "Sin resultados.", en: "No results.", fr: "Aucun résultat." })}</p>
                      ) : (
                        <div className="space-y-4">
                          {LEVELS.map((lv) => {
                            const subset = results.filter((r) => normLevel(r) === lv.key);
                            const ranking = rankBlock(subset, b);
                            if (ranking.length === 0) return null;
                            return (
                              <div key={lv.key} data-testid={`leaderboard-tier-${b.block_id}-${lv.key}`}>
                                <div className="mb-1.5 flex items-center gap-2">
                                  <span className={`rounded-full border px-2.5 py-0.5 text-[10px] font-bold ${lv.cls}`}>{lv.label}</span>
                                  <span className="text-[11px] font-medium text-muted-foreground">{ranking.length}</span>
                                </div>
                                <div className="space-y-1.5">
                                  {ranking.map((row) => (
                                    <div key={row.user_id} className="flex items-center gap-3 rounded-xl bg-secondary/40 px-3 py-2 text-sm">
                                      <span className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-bold ${row.rank && RANK_STYLE[row.rank] ? RANK_STYLE[row.rank] : "bg-secondary text-muted-foreground"}`}>
                                        {row.rank ? (row.rank <= 3 ? <Medal className="h-4 w-4" /> : row.rank) : "–"}
                                      </span>
                                      <Avatar userId={row.user_id} name={row.name} size={28} />
                                      <span className="min-w-0 flex-1 truncate font-medium">{row.name}</span>
                                      <span className="shrink-0 font-semibold">{row.raw}</span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      <div className="mt-8 rounded-2xl border border-border bg-card p-6" data-testid="wod-history-section">
        <h3 className="mb-4 flex items-center gap-2 font-display text-lg font-semibold">
          <History className="h-5 w-5 text-primary" /> {tl({ es: "Mi historial", en: "My history", fr: "Mon historique" })} ({history.length})
        </h3>
        {history.length === 0 ? (
          <p className="text-sm text-muted-foreground">{tl({ es: "Aún no tienes resultados registrados.", en: "You have no logged results yet.", fr: "Tu n'as pas encore de résultats enregistrés." })}</p>
        ) : (
          <div className="space-y-3">
            {history.map((h) => {
              const bmap = {};
              (h.blocks || []).forEach((b) => { bmap[b.block_id] = b.title; });
              return (
                <div key={h.result_id} data-testid={`wod-history-${h.result_id}`} className="rounded-xl bg-secondary/40 px-4 py-3 text-sm">
                  <div className="flex items-center justify-between">
                    <span className="font-medium">{h.title}</span>
                    <span className="flex items-center gap-2">
                      {h.level && LEVEL_MAP[h.level] && (
                        <span className={`rounded-full border px-2 py-0.5 text-[10px] font-bold ${LEVEL_MAP[h.level].cls}`}>{LEVEL_MAP[h.level].label}</span>
                      )}
                      <span className="text-xs text-muted-foreground">{h.date ? new Date(h.date + "T00:00:00").toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }) : ""}</span>
                    </span>
                  </div>
                  <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-xs">
                    {Object.entries(h.values || {}).map(([bid, val]) => val ? (
                      <span key={bid}><span className="text-muted-foreground">{bmap[bid] || ""}:</span> <span className="font-semibold">{val}</span></span>
                    ) : null)}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </AppLayout>
  );
}
